import { SubscribeClosed, type AggregateChange, type Gateway } from '@celeriant/wire';

// One SSE connection per aggregate type, rebuilt whenever the type's id set changes.
//
// Make before break: replacement streams start BEFORE the old ones abort, because
// break-before-make opened a silent poke-loss window on every id-set change. Ids that were
// already streaming then get a synthetic write poke, since their real poke may have been lost
// during the swap. A poke-driven pull is coalesced and re-derives from the cursor, so
// over-poking is safe and under-poking is not.
//
// The wire layer's subscribe iterator completes when the AbortSignal aborts, and throws
// SubscribeClosed when the EventSource permanently dies. A non-200 at reconnect (aged
// credentials, revocation, SSE cap) kills a native EventSource with no auto-reconnect, so the
// pump rebuilds with a fresh nonce after an exponential backoff. Any dispatched change resets
// the backoff.

export interface RealtimeOptions {
  gateway: Gateway;
  dispatch: (type: string, change: AggregateChange) => void;
  // The gateway caps a stream at 256 ids; more than that shards across several streams.
  maxIdsPerStream?: number;
  sleep?: (ms: number) => Promise<void>;
}

interface PerType {
  ids: Set<string>;
  // What the running pumps actually cover, as against `ids`, which is what they should cover.
  streamingIds: Set<string>;
  controllers: AbortController[];
  pumps: Promise<void>[];
  // Coalescer: a burst of subscribe/unsubscribe in one tick costs one teardown and reconnect.
  dirty: boolean;
  // Consecutive SubscribeClosed rebuilds without a successfully dispatched change.
  fatalCloses: number;
}

const DEFAULT_MAX_IDS_PER_STREAM = 256;
const REBUILD_BACKOFF_BASE_MS = 1_000;
const REBUILD_BACKOFF_CAP_MS = 30_000;

export class RealtimeMultiplexer {
  private readonly gateway: Gateway;
  private readonly dispatch: (type: string, change: AggregateChange) => void;
  private readonly maxIdsPerStream: number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly perType = new Map<string, PerType>();
  private closed = false;

  constructor(options: RealtimeOptions) {
    this.gateway = options.gateway;
    this.dispatch = options.dispatch;
    this.maxIdsPerStream = options.maxIdsPerStream ?? DEFAULT_MAX_IDS_PER_STREAM;
    this.sleep = options.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
  }

  subscribe(type: string, id: string): void {
    if (this.closed) return;
    let state = this.perType.get(type);
    if (state === undefined) {
      state = { ids: new Set(), streamingIds: new Set(), controllers: [], pumps: [], dirty: false, fatalCloses: 0 };
      this.perType.set(type, state);
    }
    if (state.ids.has(id)) return;
    state.ids.add(id);
    this.markDirty(type);
  }

  unsubscribe(type: string, id: string): void {
    if (this.closed) return;
    const state = this.perType.get(type);
    if (state === undefined) return;
    if (!state.ids.delete(id)) return;
    this.markDirty(type);
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    for (const state of this.perType.values()) {
      for (const c of state.controllers) c.abort();
      state.controllers = [];
      await Promise.all(state.pumps);
      state.pumps = [];
    }
    this.perType.clear();
  }

  private markDirty(type: string): void {
    const state = this.perType.get(type);
    if (state === undefined) return;
    if (state.dirty) return;
    state.dirty = true;
    queueMicrotask(() => {
      state.dirty = false;
      this.realize(type).catch((e) => {
        console.error('RealtimeMultiplexer.realize failed:', e);
      });
    });
  }

  private async realize(type: string): Promise<void> {
    if (this.closed) return;
    const state = this.perType.get(type);
    if (state === undefined) return;
    const oldControllers = state.controllers;
    const oldPumps = state.pumps;
    const previouslyStreaming = state.streamingIds;
    state.controllers = [];
    state.pumps = [];

    if (state.ids.size === 0) {
      // The await below can be seconds wide, a pump sleeping out its rebuild backoff, and a
      // resubscribe in that window runs another realize on this same state object. Re-check
      // before deleting, or the delete orphans live streams that close() can no longer abort:
      // a leaked SSE slot that double-dispatches on the next resubscribe.
      for (const c of oldControllers) c.abort();
      await Promise.all(oldPumps);
      if (state.ids.size === 0 && this.perType.get(type) === state) {
        this.perType.delete(type);
      }
      return;
    }

    // Old pumps are not awaited: that would block the next realize across a slow EventSource
    // close. The brief overlap can dispatch a duplicate change, which the engine tolerates,
    // its handlers being mutex-serialised and re-derived from store state.
    const allIds = Array.from(state.ids);
    for (let i = 0; i < allIds.length; i += this.maxIdsPerStream) {
      const chunk = allIds.slice(i, i + this.maxIdsPerStream);
      const ctrl = new AbortController();
      state.controllers.push(ctrl);
      state.pumps.push(this.pump(type, chunk, ctrl.signal));
    }
    state.streamingIds = new Set(state.ids);
    for (const c of oldControllers) c.abort();

    // Newly added ids need no poke: their engines just ran the open-time catch-up pull.
    for (const id of allIds) {
      if (previouslyStreaming.has(id)) {
        this.dispatch(type, { aggregateId: id, operation: 'write' });
      }
    }
  }

  private async pump(type: string, ids: string[], signal: AbortSignal): Promise<void> {
    try {
      for await (const change of this.gateway.subscribe(type, ids, { signal })) {
        if (this.closed) return;
        const state = this.perType.get(type);
        if (state !== undefined) state.fatalCloses = 0;
        try { this.dispatch(type, change); }
        catch (e) {
          // A buggy dispatch handler must not kill the pump.
          console.error('RealtimeMultiplexer.dispatch threw:', e);
        }
      }
    } catch (e) {
      if (signal.aborted || this.closed) return;
      // Rebuild on ANY non-abort error, not just SubscribeClosed: subscribe() awaits the auth
      // provider, which can itself throw, and dying here would silently end realtime for the
      // type until the next id-set change. A deterministic error hot-loops at the 30s cap, logged.
      if (!(e instanceof SubscribeClosed)) console.error('RealtimeMultiplexer.pump:', e);
      const state = this.perType.get(type);
      if (state === undefined) return;
      state.fatalCloses += 1;
      const backoff = Math.min(REBUILD_BACKOFF_CAP_MS, REBUILD_BACKOFF_BASE_MS * 2 ** (state.fatalCloses - 1));
      // close() awaits pumps after aborting their signals, so an un-racable sleep would hang
      // Celeriant.close() for up to the full backoff.
      await Promise.race([this.sleep(backoff), abortedPromise(signal)]);
      if (this.closed || signal.aborted) return;
      this.markDirty(type);
    }
  }
}

function abortedPromise(signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise((resolve) => signal.addEventListener('abort', () => resolve(), { once: true }));
}
